import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Panel } from '@/components/dashboard/Panel';
import { StackErrorPanel } from '@/components/catalog/StackErrorPanel';
import { useStackForm } from '@/hooks/useStackForm';
import { api } from '@/lib/api';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { cn } from '@/lib/utils';
import type { CatalogStack, CreateCatalogStackRequest, UpdateCatalogStackRequest } from '@/types/catalog';
import type { Endpoint } from '@/types/endpoint';

// Monaco is large; load it only when a stack form is open.
const MonacoComposeEditor = lazy(() =>
  import('@/components/catalog/MonacoComposeEditor').then((m) => ({ default: m.MonacoComposeEditor })),
);

interface StackDetailFormProps {
  stack?: CatalogStack | null;
  /** Must reject on failure: the form shows the error and keeps the edits. Resolving means the save succeeded. */
  onSave: (data: CreateCatalogStackRequest | UpdateCatalogStackRequest) => Promise<void>;
  onDelete?: () => void;
  onRestore?: () => Promise<void>;
  isNew?: boolean;
  loading?: boolean;
  /** Reports unsaved edits to the page (for leave guards). Must be a stable callback, such as a state setter. */
  onDirtyChange?: (dirty: boolean) => void;
}

export function StackDetailForm({
  stack,
  onSave,
  onDelete,
  onRestore,
  isNew = false,
  loading = false,
  onDirtyChange,
}: StackDetailFormProps) {
  const form = useStackForm(stack);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const savingRef = useRef(false);
  const blocks = usePolledData(() => api.securityBlocks.list());
  const lockedMode = form.formData.target_mode === 'locked';
  const [endpoints, setEndpoints] = useState<{ data: Endpoint[] | null; error: string | null }>({ data: null, error: null });

  const dirty = form.isDirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  // Unmounting (for example while the page reloads after a create) clears the page's dirty flag.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Locked mode needs the endpoint list. Only 'up' endpoints can be chosen.
  useEffect(() => {
    if (!lockedMode) return;
    let cancelled = false;
    api.endpoints
      .listAll()
      .then((data) => {
        if (!cancelled) setEndpoints({ data: data.filter((ep) => ep.status === 'up'), error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setEndpoints({ data: null, error: err instanceof Error && err.message ? err.message : 'Failed to load endpoints' });
      });
    return () => {
      cancelled = true;
    };
  }, [lockedMode]);

  const clientErrors = form.validate();
  const validationErrors = clientErrors.map((e) => e.message);
  if (lockedMode && form.formData.locked_endpoints.length === 0) {
    validationErrors.push('Locked mode requires at least one endpoint to be selected.');
  }
  const advancedInvalid = form.isAdvancedMode && form.advancedErrors.length > 0;
  const canSave = !loading && !saving && dirty && validationErrors.length === 0 && !advancedInvalid;

  const handleSave = async () => {
    if (savingRef.current || !canSave) return;
    savingRef.current = true;
    setSaving(true);
    setServerError(null);
    try {
      const request = form.buildRequest();
      await onSave(request);
      form.markSaved();
    } catch (err) {
      setServerError(err instanceof Error && err.message ? err.message : 'Failed to save stack');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setServerError(null);
    form.discardChanges();
  };

  const busy = loading || saving;

  return (
    <div className="space-y-6">
      {validationErrors.length > 0 && <StackErrorPanel errors={validationErrors} />}

      {serverError ? (
        <StackErrorPanel title="The server did not save this stack" errors={[serverError]} alert />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Stack name</Label>
            <Input
              id="name"
              value={form.formData.name}
              onChange={(e) => form.setName(e.target.value)}
              placeholder="small, medium, large, etc."
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">Letters, numbers, underscores, and dashes only</p>
          </div>

          <div role="group" aria-labelledby="compose-label" className="space-y-2">
            <Label id="compose-label">Compose content</Label>
            <Suspense
              fallback={
                <div role="status" className="flex h-[400px] items-center justify-center rounded-md border bg-muted/40 text-sm text-muted-foreground">
                  Loading editor…
                </div>
              }
            >
              <MonacoComposeEditor value={form.formData.compose_content} onChange={form.setComposeContent} readOnly={busy} />
            </Suspense>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Environment variables</Label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    id="advanced-mode"
                    type="checkbox"
                    checked={form.isAdvancedMode}
                    onChange={(e) => form.setAdvancedMode(e.target.checked)}
                    disabled={busy}
                    className="size-4 accent-primary"
                  />
                  <Label htmlFor="advanced-mode" className="cursor-pointer text-xs font-normal select-none">
                    Advanced mode (.env)
                  </Label>
                </div>
                {!form.isAdvancedMode && (
                  <Button type="button" size="sm" variant="outline" onClick={form.addEnvVar} disabled={busy}>
                    Add variable
                  </Button>
                )}
              </div>
            </div>

            {form.isAdvancedMode ? (
              <div className="space-y-2">
                <Label htmlFor="env-raw" className="sr-only">
                  Raw .env text
                </Label>
                <textarea
                  id="env-raw"
                  value={form.rawEnvText}
                  onChange={(e) => form.setRawEnvText(e.target.value)}
                  placeholder={`# Example:\nPORT=8080\nDB_PASS=[generate_password:16]`}
                  spellCheck={false}
                  className="min-h-[150px] w-full rounded-md border border-input bg-transparent p-3 font-mono text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={busy}
                />
                {form.advancedErrors.length > 0 && (
                  <div role="alert" className="space-y-1 rounded border border-destructive/20 bg-destructive/10 p-2 text-xs text-destructive">
                    {form.advancedErrors.map((err, idx) => (
                      <p key={idx}>{err}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : form.formData.env_vars.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">No environment variables. Turn on Advanced mode to paste raw .env text.</p>
            ) : (
              <div className="space-y-2">
                {form.formData.env_vars.map((env, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={env.key}
                      onChange={(e) => form.updateEnvVar(index, 'key', e.target.value)}
                      placeholder="KEY"
                      aria-label={`Variable ${index + 1} name`}
                      className="w-40"
                      disabled={busy}
                    />
                    <span aria-hidden className="text-muted-foreground">=</span>
                    <Input
                      value={env.value}
                      onChange={(e) => form.updateEnvVar(index, 'value', e.target.value)}
                      placeholder="value"
                      aria-label={`Variable ${index + 1} value`}
                      className="flex-1"
                      disabled={busy}
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => form.removeEnvVar(index)} disabled={busy}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-md border bg-muted/40 p-4 text-xs text-muted-foreground">
            <p className="font-semibold text-foreground">Auto-generate secrets</p>
            <p>Use placeholders in an environment variable value to generate secrets on deployment:</p>
            <ul className="list-disc space-y-1 pl-4">
              <li><code>[generate_secret]</code>: random alphanumeric string (32 characters).</li>
              <li><code>[generate_secret:16]</code>: custom length random alphanumeric string.</li>
              <li><code>[generate_password]</code>: random strong password (32 characters, containing uppercase, lowercase, numbers, and symbols).</li>
              <li><code>[generate_password:16]</code>: custom length strong password (minimum length 4).</li>
              <li><code>[generate_secret:base64:32]</code>: 32 random bytes base64 encoded (similar to <code>openssl rand -base64 32</code>).</li>
              <li><code>[generate_secret:hex:32]</code>: 32 random bytes hex encoded (similar to <code>openssl rand -hex 32</code>).</li>
            </ul>
            <p>Generated secrets are stored in the stack's environment variables and persist across redeployments.</p>
          </div>
        </div>

        <aside className="min-w-0 space-y-6" aria-label="Stack settings">
          <Panel title="Resources" loading={false} error={null} hasData>
            <div className="grid grid-cols-3 gap-2">
              <div className="min-w-0 space-y-1">
                <Label htmlFor="cpu" className="text-xs">CPU limit</Label>
                <Input
                  id="cpu"
                  type="number"
                  min={0.01}
                  max={16}
                  step={0.01}
                  value={form.formData.cpu_limit}
                  onChange={(e) => form.setCpuLimit(parseFloat(e.target.value) || 0.5)}
                  disabled={busy}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <Label htmlFor="memory" className="text-xs">Memory (MB)</Label>
                <Input
                  id="memory"
                  type="number"
                  min={64}
                  max={32768}
                  value={form.formData.memory_mb}
                  onChange={(e) => form.setMemoryMb(parseInt(e.target.value) || 512)}
                  disabled={busy}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <Label htmlFor="storage" className="text-xs">Storage (GB)</Label>
                <Input
                  id="storage"
                  type="number"
                  min={1}
                  max={1000}
                  value={form.formData.storage_gb}
                  onChange={(e) => form.setStorageGb(parseInt(e.target.value) || 10)}
                  disabled={busy}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">CPU 0.01 to 16, memory 64 to 32768 MB, storage 1 to 1000 GB.</p>

            <div className="flex items-start gap-3 pt-1">
              <input
                id="shared-cpu"
                type="checkbox"
                checked={form.formData.shared_cpu}
                onChange={(e) => form.setSharedCPU(e.target.checked)}
                disabled={busy}
                className="mt-1 size-4 accent-primary"
              />
              <div className="space-y-0.5">
                <Label htmlFor="shared-cpu" className="cursor-pointer">Shared CPU</Label>
                <p className="text-xs text-muted-foreground">
                  The CPU limit still applies at runtime, but the stack does not reserve capacity on the endpoint (oversubscribed).
                </p>
              </div>
            </div>
          </Panel>

          <Panel
            title="Security blocks"
            loading={blocks.loading}
            error={blocks.error}
            hasData={blocks.data !== undefined}
            onRetry={blocks.refetch}
          >
            <p className="text-xs text-muted-foreground">
              Toggle the overlays that apply to this stack. Defaults are pre-selected; only changes are saved.
            </p>
            {(blocks.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No security blocks configured.</p>
            ) : (
              <ul className="space-y-2">
                {(blocks.data ?? []).map((block) => {
                  const isDefault = block.is_enabled_default;
                  // Effective toggle: the stack's override if present, else the block's default.
                  const override = form.formData.security_block_overrides[block.id];
                  const checked = override !== undefined ? override : isDefault;
                  return (
                    <li key={block.id} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={`secblk-${block.id}`}
                        checked={checked}
                        onChange={(e) => form.setSecurityBlockEnabled(block.id, e.target.checked, isDefault)}
                        disabled={busy}
                        className="mt-1 size-4 accent-primary"
                      />
                      <label htmlFor={`secblk-${block.id}`} className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-sm">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium [overflow-wrap:anywhere]">{block.name}</span>
                          {isDefault ? <Badge variant="secondary">Default</Badge> : null}
                        </span>
                        {block.description ? <span className="text-xs text-muted-foreground">{block.description}</span> : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          <Panel title="Target endpoint mode" loading={false} error={null} hasData>
            <p className="text-xs text-muted-foreground">Where apps using this stack can be deployed. Inherit uses the catalog setting.</p>
            <fieldset className="space-y-2">
              <legend className="sr-only">Target endpoint mode</legend>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="target-inherit"
                  name="stack-target-mode"
                  checked={form.formData.target_mode === null}
                  onChange={() => form.setTargetModeInherit()}
                  disabled={busy}
                  className="size-4"
                />
                <label htmlFor="target-inherit" className="text-sm">Inherit from catalog</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="stack-target-auto"
                  name="stack-target-mode"
                  checked={form.formData.target_mode === 'auto'}
                  onChange={() => {
                    form.setTargetMode('auto');
                    form.setLockedEndpoints([]);
                  }}
                  disabled={busy}
                  className="size-4"
                />
                <label htmlFor="stack-target-auto" className="text-sm">Auto: all eligible endpoints</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="stack-target-locked"
                  name="stack-target-mode"
                  checked={lockedMode}
                  onChange={() => form.setTargetMode('locked')}
                  disabled={busy}
                  className="size-4"
                />
                <label htmlFor="stack-target-locked" className="text-sm">Locked: specific endpoints</label>
              </div>
            </fieldset>

            {lockedMode ? (
              <div className="space-y-2 border-l-2 border-muted pl-4">
                {endpoints.error ? (
                  <p role="alert" className="text-sm text-destructive">{endpoints.error}</p>
                ) : endpoints.data === null ? (
                  <p role="status" className="text-sm text-muted-foreground">Loading endpoints…</p>
                ) : endpoints.data.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No endpoints are up right now.</p>
                ) : (
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded border p-2">
                    {endpoints.data.map((ep) => (
                      <label key={ep.id} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.formData.locked_endpoints.includes(ep.id)}
                          onChange={(e) =>
                            form.setLockedEndpoints(
                              e.target.checked
                                ? [...form.formData.locked_endpoints, ep.id]
                                : form.formData.locked_endpoints.filter((id) => id !== ep.id),
                            )
                          }
                          disabled={busy}
                          className="size-4 accent-primary"
                        />
                        <span className="text-sm">{ep.name}</span>
                        <span className="text-xs text-muted-foreground">(ID: {ep.id})</span>
                      </label>
                    ))}
                  </div>
                )}
                {lockedMode && form.formData.locked_endpoints.length === 0 ? (
                  <p className="text-xs text-destructive">At least one endpoint must be selected in locked mode.</p>
                ) : null}
              </div>
            ) : null}
          </Panel>
        </aside>
      </div>

      {!isNew && (onDelete || onRestore) ? (
        <div className="flex gap-2 border-t pt-4">
          {stack?.is_active && onDelete ? (
            <Button variant="destructive" onClick={() => !busy && onDelete()} aria-disabled={busy}>
              Delete stack
            </Button>
          ) : null}
          {!stack?.is_active && onRestore ? (
            <Button variant="outline" onClick={() => !busy && void onRestore()} aria-disabled={busy}>
              Restore stack
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80',
        )}
      >
        <p role="status" className={cn('text-sm', dirty ? 'font-medium text-status-warning' : 'text-muted-foreground')}>
          {dirty ? 'Unsaved changes' : isNew ? 'Not created yet' : 'All changes saved'}
        </p>
        <div className="flex gap-2">
          {/* aria-disabled keeps focus on the button during a save; the handlers below do the guarding. */}
          <Button type="button" variant="outline" onClick={() => !busy && dirty && handleDiscard()} aria-disabled={busy || !dirty}>
            Discard
          </Button>
          <Button type="button" onClick={() => void handleSave()} aria-disabled={!canSave} aria-busy={saving}>
            {saving ? 'Saving…' : isNew ? 'Create stack' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
